import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
import { store } from './store/store';
import { Provider } from 'react-redux';
import * as serviceWorker from './serviceWorker';
import { BrowserRouter } from 'react-router-dom';
import { datadogLogs } from '@datadog/browser-logs';
import { ThemeProvider } from '@mui/material';
import { theme } from 'theme';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// Forward admin errors (the ErrorBoundary console.errors) to Datadog — four of
// the six July blank-screen bugs were invisible because admin had no telemetry.
// Gated on REACT_APP_ENABLE_LOGS so it ships dark; no ingestion cost until
// enabled. Scoped to warn/error only (not all levels like the member app): the
// admin surface is PHI-dense (staff sees every member), so info/log/debug are
// deliberately not forwarded. Only the manual overrides forward — no
// `forwardConsoleLogs` — to avoid double-forwarding each line.
if (process.env.REACT_APP_ENABLE_LOGS === 'true') {
  const env = process.env.REACT_APP_ENV ?? 'dev';

  datadogLogs.init({
    clientToken: 'pub9b7967d165b31c8e7670d29098d4caf2',
    site: 'datadoghq.com',
    forwardErrorsToLogs: false, // manually forwarded below
    service: `${env}-betterleave-admin`,
  });

  const { error, warn } = console;

  global.console.error = (...args: unknown[]) => {
    if (Array.isArray(args)) {
      datadogLogs.logger.error(args[0]);
    }
    error(...args);
  };
  global.console.warn = (...args: unknown[]) => {
    if (Array.isArray(args)) {
      datadogLogs.logger.warn(args[0]);
    }
    warn(...args);
  };

  console.log('Datadog Logs Enabled (warn/error only)');
} else {
  console.log('Datadog Logs Disabled');
}

ReactDOM.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Provider store={store}>
          <BrowserRouter>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </BrowserRouter>
        </Provider>
      </LocalizationProvider>
    </ThemeProvider>
  </React.StrictMode>,
  document.getElementById('root'),
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
